import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonToggleChange, MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { Log } from 'src/app/logger.service';
import { WorkforceLayoutService } from 'src/app/workforce/workforce-layout.service';

export interface ToggleOption {
  value: string;
  displayText: string;
}

/**
 * A component that displays select dropdown options on small screens and toggle options on large screens
 * For more than maxOptions options, it shows only the maxOptions most recently selected ones
 * with an ellipsis menu for the rest.
 */
@Component({
    selector: 'app-option-toggle',
    templateUrl: './option-toggle.component.html',
    styleUrls: ['./option-toggle.component.scss'],
    standalone: true,
    imports: [CommonModule, MatButtonToggleModule, MatSelectModule, MatMenuModule, MatIconModule]
})
export class OptionToggleComponent implements OnInit, OnChanges {
  @Input() options: ToggleOption[] = [];
  @Input() label: string = '';
  @Input() selectedValue: string = '';
  @Input() labelClass: string = 'tw-text-sm tw-font-medium tw-text-gray-800 dark:tw-text-gray-200';
  @Input() maxOptions: number = 2;

  @Output() selectionChange = new EventEmitter<string>();

  visibleOptions: ToggleOption[] = [];
  menuOptions: ToggleOption[] = [];

  // Track recently used options for displaying in the main toggle
  private recentlyUsed: string[] = [];

  constructor(protected layoutService: WorkforceLayoutService) {}

  ngOnInit(): void {
    this.initializeOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options'] || changes['selectedValue']) {
      this.initializeOptions();
    }
  }

  private initializeOptions(): void {
    Log.d('initializeOptions', this.options, this.selectedValue);

    if (!this.options || this.options.length === 0) {
      this.visibleOptions = [];
      this.menuOptions = [];
      return;
    }

    // Default to first option if no selection
    if (!this.selectedValue && this.options.length > 0) {
      this.selectedValue = this.options[0].value;
    }

    // Add selected value to recently used if not already there
    if (this.selectedValue && !this.recentlyUsed.includes(this.selectedValue)) {
      this.recentlyUsed.unshift(this.selectedValue);
      if (this.recentlyUsed.length > this.maxOptions) {
        this.recentlyUsed = this.recentlyUsed.slice(0, this.maxOptions);
      }
    }

    // Initialize recently used if empty
    if (this.recentlyUsed.length === 0 && this.options.length > 0) {
      this.recentlyUsed = [this.options[0].value];
      if (this.options.length > 1) {
        this.recentlyUsed.push(this.options[1].value);
      }
    }

    this.updateDisplayedOptions();
  }

  // Updates which options are visible in the toggle vs. in the menu
  private updateDisplayedOptions(): void {
    // If we have maxOptions or fewer options, just show them all
    if (this.options.length <= this.maxOptions) {
      this.visibleOptions = [...this.options];
      this.menuOptions = [];
      return;
    }

    // Find the options matching our recently used values
    this.visibleOptions = [];
    for (const recentValue of this.recentlyUsed) {
      const option = this.options.find((opt) => opt.value === recentValue);
      if (option) {
        this.visibleOptions.push(option);
      }
    }

    // If somehow we don't have maxOptions visible options, add options until we do
    if (this.visibleOptions.length === 0) {
      this.visibleOptions = [this.options[0]];
      this.recentlyUsed = [this.options[0].value];
    }

    if (this.visibleOptions.length === 1 && this.options.length > 1) {
      // Find an option not already visible
      const nextOption = this.options.find(
        (opt) => !this.visibleOptions.some((visOpt) => visOpt.value === opt.value)
      );
      if (nextOption) {
        this.visibleOptions.push(nextOption);
        if (!this.recentlyUsed.includes(nextOption.value)) {
          this.recentlyUsed.push(nextOption.value);
        }
      }
    }

    // All remaining options go to the menu
    this.menuOptions = this.options.filter(
      (option) => !this.visibleOptions.some((visOpt) => visOpt.value === option.value)
    );
  }

  // for toggle button changes on larger screens
  onToggleChange(event: MatButtonToggleChange): void {
    // skip 'more' button, which opens a menu
    if (event.value === 'more') {
      return;
    }
    this._processSelection(event.value);
  }

  // option selected from the 'more' menu on larger screens
  onMenuOptionSelected(option: ToggleOption): void {
    this._processSelection(option.value);
  }

  // for mat-select option selection on mobile
  onSelectChange(event: MatSelectChange): void {
    this._processSelection(event.value);
  }

  // Single handler for all 3 controls (toggle, select, menu)
  private _processSelection(newValue: string): void {
    const oldValue = this.selectedValue;

    // Do nothing if the value hasn't really changed.
    if (oldValue === newValue) {
      return;
    }

    this.selectedValue = newValue;

    // Update the recently used list to bring the new selection to the front.
    this.recentlyUsed = this.recentlyUsed.filter((val) => val !== this.selectedValue);
    this.recentlyUsed.unshift(this.selectedValue);
    if (this.recentlyUsed.length > this.maxOptions) {
      this.recentlyUsed = this.recentlyUsed.slice(0, this.maxOptions);
    }

    this.updateDisplayedOptions();

    // Only emit if the value actually changed
    this.selectionChange.emit(this.selectedValue);
  }

  // for the more button to stop propagation
  handleMoreClick(event: MouseEvent): void {
    event.stopPropagation();
  }
}
